import { newRelicNoticeError } from '../../hooks/useNewRelic';
import { LessonAttachment } from '../../services/backendService/backendService';
import { Slide, SlideElement } from './slide.types';
import { generateUniqueId } from './slideUtil';

export type CuripodPasteType<T> = {
  curipodCopySlideVersion: string;
  value: T;
};

const toVersionedJSONPayload = <T>(value: T): string => {
  return JSON.stringify({
    curipodCopySlideVersion: '2.0',
    value,
  });
};

export const parseClipboardData = <T>(
  data: string,
): CuripodPasteType<{ type: string; payload: T; tenantId?: string }> | undefined => {
  try {
    const result = JSON.parse(data) as CuripodPasteType<{
      type: string;
      payload: T;
      tenantId?: string;
    }>;
    return result;
  } catch (e) {
    // if any error occurs, we just return false.
    return undefined;
  }
};

export const makeNewIdsForSlide = (slides: Slide[]): Slide[] => {
  const newIds = slides.map(() => generateUniqueId().toString());
  const newSlides = slides.map((slide, i, all) => {
    // `findIndex` returns -1 when the referenced slide isn't part of this batch.
    // -1 is a valid array index in JS, so the old `refId !== undefined` guard
    // let a `-1` through and read `newIds[-1]`, which silently evaluates to
    // `undefined` instead of clearing the link to `null` like retryOfSlideId
    // does below (see the `>= 0` check there). A stray `undefined` still reads
    // as "no reference" everywhere in-memory, but a plain object spread keeps
    // an `undefined` key while `JSON.stringify` (used to save the template,
    // and to build the clipboard payload) silently drops it — so the field
    // goes missing from the payload instead of being explicitly nulled out.
    const refId = slide.dataReferenceSlideId
      ? all.findIndex(r => r.id === slide.dataReferenceSlideId)
      : -1;

    const retryIdx = slide.retryOfSlideId
      ? all.findIndex(r => r.id === slide.retryOfSlideId)
      : -1;

    const pollOptions = slide.pollOptions
      ? slide.pollOptions.map(opt => ({ ...opt, id: generateUniqueId().toString() }))
      : undefined;
    const elements = (slide.elements || ([] as SlideElement[]))?.map(
      (el: SlideElement) => {
        const newId = generateUniqueId().toString();
        return {
          ...el,
          id: newId,
        };
      },
    );

    const contextElements = slide.contextElements?.map(el => {
      const oldElementIndex = (slide.elements || []).findIndex(
        e => e.id === el.elementId,
      );
      const newId = elements[oldElementIndex]?.id;
      if (!newId) {
        throw new Error('Broken slides - element not found when creating a new slide Id');
      }

      return {
        ...el,
        elementId: newId,
      };
    });

    return {
      ...slide,
      id: newIds[i],
      dataReferenceSlideId: refId >= 0 ? newIds[refId] : null,
      retryOfSlideId: retryIdx >= 0 ? newIds[retryIdx] : null,
      pollOptions,
      elements,
      contextElements,
    };
  });

  return newSlides;
};

/**
 * Slides are used to persist the actual data
 * TenantId is needed to handle images compatibility.
 * For now we will not support copy pasting slides across different tenants because we
 * cannot guarantee that images are present in the clipboard.
 * But as long as the image is on the same tenant - it does not matter which game or template
 * the image is related to.
 * This function returns true if writing to clipboard was a success, otherwise false
 */
export const copySlide = async ({
  slides,
  tenantId,
  templateId,
  attachments,
}: {
  slides: Slide[];
  tenantId: string;
  templateId?: string;
  attachments?: LessonAttachment[];
}): Promise<boolean> => {
  if (typeof window === 'undefined') {
    // Prevent SSR side effects
    return false;
  }
  try {
    // Collect all unique attachment IDs from the slides
    const attachmentIds = new Set<string>();
    slides.forEach(slide => {
      slide.elements?.forEach(element => {
        if (element.type === 'attachmentPassage' && element.attachmentId) {
          attachmentIds.add(element.attachmentId);
        }
      });
    });

    // Filter attachments to only include those referenced by the slides
    const relevantAttachments = attachments?.filter(att =>
      attachmentIds.has(att.attachmentId),
    );

    const generatedPayload = toVersionedJSONPayload({
      payload: slides,
      tenantId,
      templateId,
      attachments: relevantAttachments,
      type: 'slide',
    });

    await navigator.clipboard.writeText(generatedPayload);

    return true;
  } catch (e) {
    newRelicNoticeError(e, 'Error while copying a slide');

    // Some error occurred while copying a slide;
    return false;
  }
};
export const copyElement = async ({
  elements,
  slideId,
}: {
  elements: SlideElement[];
  slideId: string;
}): Promise<boolean> => {
  if (typeof window === 'undefined') {
    // Prevent SSR side effects
    return false;
  }
  try {
    if (elements.some(e => e.isPollOption)) return false;
    const generatedPayload = toVersionedJSONPayload({
      payload: { elements, slideId },
      type: 'elements',
    });

    await navigator.clipboard.writeText(generatedPayload);

    return true;
  } catch (e) {
    newRelicNoticeError(e, 'Error while copying a slide element');

    // Some error occurred while copying a slide;
    return false;
  }
};

/**
 *
 * @param tenantId used to prevent pasting content across tenants
 */

export const retrieveClipBoardText = async () => {
  if (typeof window === 'undefined') {
    // Prevent SSR side effects
    return undefined;
  }
  try {
    const clipboardText = await navigator.clipboard.readText();
    return clipboardText;
  } catch (e) {
    newRelicNoticeError(e, 'Error while retrieving clipboard text');
    // Some error occurred while copying a slide;
    return undefined;
  }
};
export const retrieveClipBoardImage = async () => {
  if (typeof window === 'undefined') {
    // Prevent SSR side effects
    return undefined;
  }
  try {
    const clipboardItems = await navigator.clipboard.read();
    return clipboardItems;
  } catch (e) {
    newRelicNoticeError(e, 'Error while retrieving clipboard image');

    // Some error occurred while copying a slide;
    return undefined;
  }
};
